import React, { useState, useRef } from "react";
import { X, UploadCloud, FileText, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  StaffDocumentType,
  StaffDocumentTypeLabels,
} from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";

interface StaffDocumentUploadModalProps {
  isOpen: boolean;
  staffId: string;
  staffName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const StaffDocumentUploadModal: React.FC<StaffDocumentUploadModalProps> = ({
  isOpen,
  staffId,
  staffName,
  onClose,
  onSuccess,
}) => {
  const [documentType, setDocumentType] = useState<StaffDocumentType>(
    StaffDocumentType.AADHAAR
  );
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (file: File | undefined) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit");
      return;
    }
    setSelectedFile(file);
    if (!documentName) {
      // Pre-fill document name based on file basename
      const cleanName = file.name.replace(/\.[^/.]+$/, "");
      setDocumentName(cleanName);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("documentType", documentType);
      if (documentName.trim()) {
        formData.append("documentName", documentName.trim());
      }

      const res = await ClinicStaffService.uploadStaffDocument(staffId, formData);
      if (res.success) {
        toast.success(res.message || "Document uploaded successfully");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to upload document");
      }
    } catch (error) {
      console.error("Upload document error:", error);
      toast.error("Error uploading document");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Upload Staff Document
            </h3>
            {staffName && (
              <p className="text-xs text-gray-500 mt-0.5">
                For: <span className="font-medium text-gray-700">{staffName}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Document Type Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as StaffDocumentType)}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              required
            >
              {Object.values(StaffDocumentType).map((type) => (
                <option key={type} value={type}>
                  {StaffDocumentTypeLabels[type]}
                </option>
              ))}
            </select>
          </div>

          {/* Document Name / Title */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Document Title / Label
            </label>
            <input
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="e.g. Pharmacy License 2026, ID Front & Back"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
            />
          </div>

          {/* File Dropzone */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Select File <span className="text-red-500">*</span>
            </label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-emerald-500 bg-emerald-50/50"
                  : selectedFile
                  ? "border-emerald-200 bg-emerald-50/20"
                  : "border-gray-200 hover:border-emerald-400 bg-gray-50/50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
              />

              {selectedFile ? (
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                    {selectedFile.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • Click or drag to replace
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="w-10 h-10 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center">
                    <UploadCloud className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    Click to browse or drag file here
                  </p>
                  <p className="text-xs text-gray-400">
                    PDF, JPG, PNG up to 10MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
